import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const L = ({ t, hint, children }) => (
  <div><Label className="text-xs text-zinc-400 mb-1.5 block">{t}</Label>{children}{hint && <p className="text-[11px] text-zinc-500 mt-1">{hint}</p>}</div>
);

export function FinanceConfigTab({ canManage }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(false);
  const load = () => {
    setErr(false);
    api.get("/boleto/finance-config").then(({ data }) => setF({ ...data, terms: data.terms.join(", "), special_brands: data.special_brands.join(", ") }))
      .catch((e) => { setErr(true); toast.error(formatApiError(e.response?.data?.detail)); });
  };
  useEffect(() => { load(); }, []);
  if (err) return <div className="alpha-card p-8 text-center" data-testid="fin-load-error"><p className="text-zinc-400">Não foi possível carregar os parâmetros.</p><Button data-testid="fin-retry" variant="ghost" onClick={load} className="mt-3 text-gold-bright">Tentar novamente</Button></div>;
  if (!f) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" /></div>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        entry_pct: parseFloat(f.entry_pct) || 0, rate_pct: parseFloat(f.rate_pct) || 0, limit: parseFloat(f.limit) || 0,
        terms: String(f.terms).split(/[,;\s]+/).map((t) => parseInt(t)).filter((t) => t > 0),
        special_brands: String(f.special_brands).split(/[,;]+/).map((b) => b.trim()).filter(Boolean),
      };
      const { data } = await api.put("/boleto/finance-config", payload);
      setF({ ...data, terms: data.terms.join(", "), special_brands: data.special_brands.join(", ") });
      toast.success("Parâmetros de financiamento salvos");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="alpha-card p-6 max-w-3xl space-y-5" data-testid="boleto-finance-config">
      <div>
        <h2 className="text-white font-display font-bold">Simulação de financiamento (Android)</h2>
        <p className="text-sm text-zinc-400 mt-1">Entrada = % sobre o valor até o limite financiável + todo o valor que passar do limite. Parcelas fixas com juros compostos sobre o saldo.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <L t="Entrada padrão (%)"><Input data-testid="fin-entry-pct" type="number" disabled={!canManage} value={f.entry_pct} onChange={set("entry_pct")} className="i" /></L>
        <L t="Juros estimados (% ao mês)"><Input data-testid="fin-rate-pct" type="number" disabled={!canManage} value={f.rate_pct} onChange={set("rate_pct")} className="i" /></L>
        <L t="Limite máximo financiável (R$)"><Input data-testid="fin-limit" type="number" disabled={!canManage} value={f.limit} onChange={set("limit")} className="i" /></L>
        <L t="Prazos de parcelamento" hint="Separados por vírgula. Ex.: 6, 9, 12"><Input data-testid="fin-terms" disabled={!canManage} value={f.terms} onChange={set("terms")} className="i" /></L>
        <div className="sm:col-span-2">
          <L t="Marcas com regra especial (sem simulação, consulta de parcelamento sem entrada)" hint="Separadas por vírgula. Aparelhos com “iPhone” no modelo também entram nessa regra automaticamente.">
            <Input data-testid="fin-special-brands" disabled={!canManage} value={f.special_brands} onChange={set("special_brands")} className="i" />
          </L>
        </div>
      </div>
      <p className="text-[11px] text-zinc-500">Para um aparelho específico, use “Ajuste manual de financiamento” no cadastro dele. O ajuste manual não é alterado por mudanças aqui.</p>
      {canManage && <Button data-testid="fin-save" onClick={save} disabled={saving} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>{saving ? <Loader2 size={16} className="animate-spin" /> : "Salvar parâmetros"}</Button>}
    </div>
  );
}

export function ManualFinance({ f, setF }) {
  const [terms, setTerms] = useState([6, 9, 12]);
  useEffect(() => { api.get("/boleto/finance-config").then(({ data }) => setTerms(data.terms)).catch(() => {}); }, []);
  const mi = f.manualInstallments || {};
  const shown = [...new Set([...terms, ...Object.keys(mi).map(Number).filter((n) => n > 0)])].sort((a, b) => a - b);
  return (
    <div className="sm:col-span-2 border-t border-white/10 pt-3" data-testid="boleto-manual-finance">
      <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
        <input type="checkbox" data-testid="boleto-manual-toggle" checked={!!f.manualFinance} onChange={(e) => setF({ ...f, manualFinance: e.target.checked })} className="accent-[#D4AF37]" />
        Ajuste manual de financiamento (Android) — substitui o cálculo automático deste aparelho
      </label>
      {f.manualFinance && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <L t="Entrada (R$)"><Input data-testid="boleto-manual-entry" type="number" value={f.manualEntry} onChange={(e) => setF({ ...f, manualEntry: e.target.value })} className="i" /></L>
          {shown.map((t) => (
            <L key={t} t={`Parcela ${t}x (R$)`}>
              <Input data-testid={`boleto-manual-inst-${t}`} type="number" value={mi[String(t)] ?? ""} onChange={(e) => setF({ ...f, manualInstallments: { ...mi, [String(t)]: e.target.value } })} className="i" />
            </L>
          ))}
        </div>
      )}
    </div>
  );
}
